import { useEffect, useState } from "react";
import {
  getAdminUsers,
  getAdminUserDetail,
  AdminUserRow,
  AdminUserDetail,
  inviteUser,
  revokeInvite,
  blockUser,
  unblockUser,
  deleteUserPermanently,
} from "../../lib/adminApi";
import { formatBytes, formatUsd, labelForEvent } from "../../lib/adminLabels";
import { AdminNav } from "./AdminNav";
import { ConfirmPermanentDelete } from "../../components/ConfirmPermanentDelete";
import { Toast } from "../../components/Toast";
import { supabase } from "../../lib/supabase";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Card } from "../../ui/Card";
import { Skeleton } from "../../ui/Skeleton";
import { EmptyState } from "../../ui/EmptyState";

function formatJoined(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Admin-specific status badge (active/blocked/pending — different from meeting statuses)
// ---------------------------------------------------------------------------

const ADMIN_STATUS_COLORS: Record<AdminUserRow["status"], { bg: string; color: string; border: string }> = {
  active:  { bg: "var(--ok-soft)",     color: "var(--ok)",     border: "1px solid color-mix(in srgb, var(--ok) 30%, transparent)" },
  blocked: { bg: "var(--danger-soft)", color: "var(--danger)", border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)" },
  pending: { bg: "var(--warn-soft)",   color: "var(--warn)",   border: "1px solid color-mix(in srgb, var(--warn) 30%, transparent)" },
};

const STATUS_LABEL: Record<AdminUserRow["status"], string> = {
  active: "Active",
  blocked: "Blocked",
  pending: "Pending",
};

function AdminStatusChip({ status }: { status: AdminUserRow["status"] }) {
  const { bg, color, border } = ADMIN_STATUS_COLORS[status];
  return (
    <span
      style={{
        display: "inline-block",
        fontSize: "var(--text-xs)",
        fontWeight: 600,
        padding: "3px 9px",
        borderRadius: 999,
        background: bg,
        color,
        border,
        letterSpacing: "0.02em",
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Detail panel (unchanged)
// ---------------------------------------------------------------------------

function UserDetailPanel({ userId }: { userId: string }) {
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdminUserDetail(userId)
      .then((d) => {
        if (!cancelled) {
          setDetail(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) return <p className="muted" style={{ margin: "8px 0 0 0", fontSize: 13 }}>Loading activity…</p>;
  if (error) return <p className="error" style={{ margin: "8px 0 0 0", fontSize: 13 }}>{error}</p>;
  if (!detail) return null;

  const entries = Object.entries(detail.events_by_type);
  if (entries.length === 0) {
    return <p className="muted" style={{ margin: "8px 0 0 0", fontSize: 13 }}>No events recorded.</p>;
  }

  entries.sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 12, color: "var(--ink-3)", marginBottom: 6 }}>Activity breakdown</div>
      <table className="meetings" style={{ width: "auto" }}>
        <thead>
          <tr>
            <th style={{ paddingLeft: 0 }}>Action</th>
            <th style={{ textAlign: "end" }}>Count</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([evType, count]) => (
            <tr key={evType}>
              <td style={{ paddingLeft: 0, fontSize: 13 }}>{labelForEvent(evType)}</td>
              <td style={{ textAlign: "end", fontSize: 13 }}>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserTableRow
// ---------------------------------------------------------------------------

interface UserRowProps {
  row: AdminUserRow;
  currentUserId: string | null;
  onBlock: (id: string) => void;
  onUnblock: (id: string) => void;
  onRequestDelete: (row: AdminUserRow) => void;
  onRevoke: (id: string) => void;
  mutatingId: string | null;
}

function UserTableRow({
  row,
  currentUserId,
  onBlock,
  onUnblock,
  onRequestDelete,
  onRevoke,
  mutatingId,
}: UserRowProps) {
  const [expanded, setExpanded] = useState(false);
  const isSelf = currentUserId === null || row.user_id === currentUserId;
  const busy = mutatingId === row.user_id;

  // Clicking anywhere on the data cells toggles detail; action buttons stop propagation.
  function handleRowClick(e: React.MouseEvent) {
    // Don't toggle expand when clicking a button inside the row
    if ((e.target as HTMLElement).closest("button")) return;
    setExpanded((v) => !v);
  }

  return (
    <>
      <tr
        style={{ cursor: "pointer" }}
        onClick={handleRowClick}
        aria-expanded={expanded}
      >
        <td style={{ fontSize: 13 }}>{row.email ?? <span className="muted">(no email)</span>}</td>
        <td style={{ fontSize: 13 }}>{formatJoined(row.joined_at)}</td>
        <td style={{ fontSize: 13, textAlign: "end" }}>{row.transcript_count}</td>
        <td style={{ fontSize: 13, textAlign: "end" }}>{formatBytes(row.audio_bytes)}</td>
        <td style={{ fontSize: 13, textAlign: "end" }}>{row.run_count}</td>
        <td style={{ fontSize: 13, textAlign: "end" }}>{formatUsd(row.spend_usd)}</td>
        <td style={{ fontSize: 12, textAlign: "center" }}>
          <AdminStatusChip status={row.status} />
        </td>
        <td style={{ fontSize: 12, textAlign: "end", whiteSpace: "nowrap" }}>
          {row.status === "active" && !isSelf && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); onBlock(row.user_id); }}
            >
              Block
            </Button>
          )}
          {row.status === "blocked" && (
            <div style={{ display: "inline-flex", gap: 6 }}>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={(e) => { e.stopPropagation(); onUnblock(row.user_id); }}
              >
                Unblock
              </Button>
              {!isSelf && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={busy}
                  onClick={(e) => { e.stopPropagation(); onRequestDelete(row); }}
                >
                  Delete permanently
                </Button>
              )}
            </div>
          )}
          {row.status === "pending" && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={(e) => { e.stopPropagation(); onRevoke(row.user_id); }}
            >
              Revoke
            </Button>
          )}
        </td>
        <td style={{ fontSize: 12, color: "var(--ink-3)", textAlign: "end" }}>
          {expanded ? "▲" : "▼"}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={9} style={{ background: "var(--surface-2)", paddingTop: 12, paddingBottom: 12 }}>
            <UserDetailPanel userId={row.user_id} />
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// AdminUsers (main page)
// ---------------------------------------------------------------------------

export function AdminUsers() {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Mutation busy state (row-level)
  const [mutatingId, setMutatingId] = useState<string | null>(null);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<AdminUserRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Toast for r2 warnings
  const [toast, setToast] = useState<string | null>(null);

  // Load the signed-in user's id once
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data?.user?.id ?? null);
    });
  }, []);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const d = await getAdminUsers();
      setRows(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function handleInvite() {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      await inviteUser(inviteEmail.trim());
      setInviteEmail("");
      await loadUsers();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : String(e));
    } finally {
      setInviteBusy(false);
    }
  }

  async function handleBlock(id: string) {
    setMutatingId(id);
    try {
      await blockUser(id);
      await loadUsers();
    } catch (e) {
      setToast(`Failed to block user: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setMutatingId(null);
    }
  }

  async function handleUnblock(id: string) {
    setMutatingId(id);
    try {
      await unblockUser(id);
      await loadUsers();
    } catch (e) {
      setToast(`Failed to unblock user: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setMutatingId(null);
    }
  }

  async function handleRevoke(id: string) {
    setMutatingId(id);
    try {
      await revokeInvite(id);
      await loadUsers();
    } catch (e) {
      setToast(`Failed to revoke invite: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setMutatingId(null);
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const result = await deleteUserPermanently(deleteTarget.user_id);
      setDeleteTarget(null);
      if (result.r2_delete_warning) {
        setToast(result.r2_delete_warning);
      }
      await loadUsers();
    } catch (e) {
      setToast(`Failed to delete user: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <AdminNav />
      <h2 style={{ marginTop: 0, marginBottom: 20 }}>Users</h2>

      {/* Invite form */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
        <Input
          type="email"
          placeholder="Invite by email"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          disabled={inviteBusy}
          style={{ minWidth: 240 }}
          onKeyDown={(e) => { if (e.key === "Enter") handleInvite(); }}
        />
        <Button variant="primary" size="sm" onClick={handleInvite} disabled={inviteBusy || !inviteEmail.trim()}>
          {inviteBusy ? "Inviting…" : "Invite"}
        </Button>
        {inviteError && (
          <span className="error" style={{ fontSize: 13 }}>{inviteError}</span>
        )}
      </div>

      {error && <p className="error" style={{ marginBottom: 12 }}>{error}</p>}

      {loading ? (
        <Card style={{ padding: 16 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "center", paddingBottom: 14 }}>
              <Skeleton height={13} width="28%" />
              <Skeleton height={13} width="14%" />
              <Skeleton height={13} width="8%" />
              <Skeleton height={13} width="8%" />
              <Skeleton height={13} width="8%" />
            </div>
          ))}
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState icon="user" title="No users yet" description="Invite someone to get started." />
      ) : (
        <Card style={{ padding: 0, overflowX: "auto" }}>
          <table className="meetings">
            <thead>
              <tr>
                <th>Email</th>
                <th>Joined</th>
                <th style={{ textAlign: "end" }}>Transcripts</th>
                <th style={{ textAlign: "end" }}>Storage</th>
                <th style={{ textAlign: "end" }}>Runs</th>
                <th style={{ textAlign: "end" }}>Spend</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "end" }}>Actions</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <UserTableRow
                  key={row.user_id}
                  row={row}
                  currentUserId={currentUserId}
                  onBlock={handleBlock}
                  onUnblock={handleUnblock}
                  onRequestDelete={setDeleteTarget}
                  onRevoke={handleRevoke}
                  mutatingId={mutatingId}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ConfirmPermanentDelete
        open={deleteTarget !== null}
        itemKind="user"
        itemName={deleteTarget ? (deleteTarget.email ?? deleteTarget.user_id) : ""}
        busy={deleteBusy}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}
